import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { delay } from 'redux-saga';
import { call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { errorHandle, FatalError } from '$app-web/utils';
import { initializeAppDateTime } from '$app-web/utils/app-date-time/utils';

import AndroidFormat from '../android-formatter';
import languages from '../languages';
import { loadNamespaceTranslations } from '../load-namespace-translations';
import {
    duckActionCreators as i18nActionCreators,
    duckActions as i18nActions,
    duckSelectors as i18nSelectors,
    Store,
} from './i18n.duck';

export const SAGA_NAME = 'I18N';

function* setI18nextLanguage() {
    const languageCode = (yield select((state: Store) => i18nSelectors.languageSelector(state))) as string;
    const namespace = (yield select((state: Store) => i18nSelectors.namespaceSelector(state))) as string;

    // Loads the language bundle (+ en-US fallback), sets the default namespace, and switches
    // the active language/date-time locale. Shared with the standalone chat iframe.
    yield call(loadNamespaceTranslations, languageCode, namespace);
}

function* checkAndSetLanguage(language: string) {
    const namespace = (yield select(i18nSelectors.namespaceSelector)) as string;

    let finalLanguage = language;
    if (languages.languageExist(language)) {
        const normalizedFinalLanguage = languages.normalizeSupportedLanguage(language);
        if (normalizedFinalLanguage === null) {
            finalLanguage = 'en-US';
        } else if (namespace !== 'public' && !languages.get(normalizedFinalLanguage).namespaceHasTranslations(namespace)) {
            /*
                Currently we don't have translations in public namespace and if we don't protect the check from public space
                the urls app/public/activity/ACTIVITY_TYPE/SESSION_UUID?lang=languageCode will not work. This is a fix for it.
             */
            finalLanguage = 'en-US';
        }
    } else {
        finalLanguage = 'en-US';
    }

    yield put(i18nActionCreators.setLanguage(languages.normalizeSupportedLanguage(finalLanguage)));
    yield call(setI18nextLanguage);
}

function* changeNamespace(action: { payload: { namespace: string } }) {
    const { namespace } = action.payload;
    const languageCode = (yield select((state: Store) => i18nSelectors.languageSelector(state))) as string;
    const namespaceIndex = i18next.options?.ns?.indexOf(namespace) ?? -1;

    if (namespaceIndex >= 0) {
        yield put(i18nActionCreators.setNamespace(namespace));
        yield call(checkAndSetLanguage, languageCode);
    } else {
        throw new FatalError(`Given namespace not exist. Namespace: ${namespace}`);
    }
}

function* changeLanguage(action: { payload: { languageCode: string } }) {
    const { languageCode } = action.payload;
    yield call(checkAndSetLanguage, languageCode);
}

function* initializeI18next() {
    yield i18next
        // @ts-expect-error: Should be deprecated after removing Redux
        .use(AndroidFormat)
        .use(initReactI18next)
        .init({
            debug: false,
            fallbackLng: 'en-US',
            whitelist: [
                'en-US',
                'de-DE',
                'fa-IR',
                'fr-CA',
                'he-IL',
                'it-IT',
                'ko-KR',
                'nl-NL',
                'ur-PK',
                'es-ES',
                'es-CO',
                'es-US',
                'ca-ES',
                'sl-SI',
                'cy-GB',
                'pt-PT',
                'zu-ZU',
                'zh-Hans',
            ],
            load: 'currentOnly',
            ns: ['pdash', 'rdash', 'webActivities'],
            keySeparator: false,
            // Ensure fallback works for missing keys
            saveMissing: false,
            missingKeyHandler: false,
            parseMissingKeyHandler: (key) => {
                // Return the key itself to trigger fallback
                return key;
            },
        });
}

function* askSpaceForLanguageNamespace(maxRetries = 2) {
    let attempts = 0;
    while (attempts < maxRetries) {
        yield put(i18nActionCreators.askSpaceForLanguageNamespace());
        // @ts-expect-error: Should be deprecated after removing Redux
        const result = yield race({
            success: take(i18nActions.ASK_SPACE_FOR_LANGUAGE_NAMESPACE_SUCCEEDED),
        });

        if (result.success) {
            const {
                payload: { namespace },
            } = result.success;
            yield put(i18nActionCreators.setNamespace(namespace));
            return;
        }

        attempts++;
        if (attempts < maxRetries) {
            yield delay(1000);
        }
    }

    throw new FatalError(`Timeout occurred during askSpaceForLanguageNamespace after ${maxRetries} attempts.`);
}

function* initialize(userLanguage: string) {
    try {
        yield call(initializeI18next);
        yield call(askSpaceForLanguageNamespace);
        yield call(initializeAppDateTime);
        yield call(checkAndSetLanguage, userLanguage);
        yield put(i18nActionCreators.initializationSucceeded());
    } catch (error) {
        errorHandle.anError(error as Error);
        yield put(i18nActionCreators.initializationFailed());
    }
}

export default function* i18nSaga() {
    try {
        const {
            payload: { userLanguage },
        } = yield take(i18nActions.INITIALIZE);
        yield call(initialize, userLanguage);
        // @ts-expect-error: Should be deprecated after removing Redux
        yield takeEvery(i18nActions.CHANGE_LANGUAGE, changeLanguage);
        // @ts-expect-error: Should be deprecated after removing Redux
        yield takeEvery(i18nActions.CHANGE_NAMESPACE, changeNamespace);
    } catch (error) {
        errorHandle.anError(error as Error);
    }
}
